import { authenticatedFetch } from "@/lib/auth/authenticatedFetch";
import { getKeycloakInstance } from "@/lib/auth/keycloak";
import { ApiResponseError, extractApiError } from "@/lib/errors/apiError";
import { resolveApiError } from "@/lib/errors/resolveApiError";
import { getApiBaseUrl } from "@/lib/utils";
import { type UseQueryOptions, queryOptions } from "@tanstack/react-query";

import {
  type BucketWithTypeAndAssignee,
  PRIVILEGED_GROUPS,
  type UserGroupFilterValue,
  bucketWithTypeAndAssigneeSchema,
  userGroupFilterValueSchema,
} from "@workspace/schemas/zod";

type BucketsWithAssigneesForDayResponse = BucketWithTypeAndAssignee[];

export function bucketsWithAssigneesForDayQueryOptions(
  day?: string,
  options: Omit<UseQueryOptions<BucketsWithAssigneesForDayResponse>, "queryKey" | "queryFn"> = {},
) {
  const keycloak = getKeycloakInstance();
  const userGroups = keycloak.tokenParsed?.group as string[] | undefined;
  const isPrivileged = userGroups?.some((g) => (PRIVILEGED_GROUPS as readonly string[]).includes(g)) ?? false;
  // not including the 'day ?? "all"' in the baseKey since it causes lint error in the queryOptions because it gives the error: "The following dependencies are missing in your queryKey: day"
  const baseKey = ["buckets", "with-assignees"] as const;
  const sharedOptions = {
    refetchInterval: 60_000,
    staleTime: 60_000,
    ...options,
  } as const;

  if (isPrivileged) {
    return queryOptions({
      queryKey: [...baseKey, day ?? "all", "all-groups"],
      queryFn: ({ signal }) => fetchBuckets(day, [], signal),
      ...sharedOptions,
    });
  }

  const validGroups = userGroups ? filterToTeamGroups(userGroups) : [];

  if (validGroups.length === 0) {
    return queryOptions({
      queryKey: [...baseKey, day ?? "all", "no-team"],
      queryFn: () => Promise.resolve([] as BucketsWithAssigneesForDayResponse),
      ...sharedOptions,
    });
  }

  return queryOptions({
    queryKey: [...baseKey, day ?? "all", { groups: validGroups }],
    queryFn: ({ signal }) => fetchBuckets(day, validGroups, signal),
    ...sharedOptions,
  });
}

function filterToTeamGroups(groups: string[]): UserGroupFilterValue[] {
  return groups.filter((g): g is UserGroupFilterValue => userGroupFilterValueSchema.safeParse(g).success);
}

async function fetchBuckets(day?: string, groups: UserGroupFilterValue[] = [], signal?: AbortSignal) {
  const baseUrl = getApiBaseUrl();
  const url = new URL(`${baseUrl}/buckets/with-assignees`);

  if (day) {
    url.searchParams.set("day", day);
  }

  for (const group of groups) {
    url.searchParams.append("groups", group);
  }

  const response = await authenticatedFetch(url.toString(), { signal });

  if (!response.ok) {
    const apiError = await extractApiError(response);
    const userMessage = resolveApiError(apiError.error_code, "Buckets konnten nicht geladen werden");
    throw new ApiResponseError(response.status, apiError.error_code, apiError.details, userMessage);
  }

  // Type assertion needed because z.infer doesn't properly type date fields from callback overrides
  return bucketWithTypeAndAssigneeSchema.array().parse(await response.json()) as unknown as BucketWithTypeAndAssignee[];
}
